import {
  collection,
  query,
  getDocs,
  getDoc,
  orderBy,
  where,
  doc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Group, NewGroup, ZoneTopic, ZoneType } from '../types';

export interface FetchGroupsOptions {
  search?: string;
  topic?: ZoneTopic;
  type?: ZoneType;
}

export async function fetchGroups(options: FetchGroupsOptions = {}): Promise<Group[]> {
  const { search, topic, type } = options;
  const groupsRef = collection(db, 'groups');

  let q = query(groupsRef, orderBy('membersCount', 'desc'));

  // Filter by type if specified
  if (type) {
    q = query(groupsRef, where('type', '==', type), orderBy('membersCount', 'desc'));
  }

  const snapshot = await getDocs(q);
  let groups: Group[] = [];

  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    groups.push({
      id: docSnap.id,
      ...data,
      // Provide default type for legacy groups without type
      type: data.type || 'general',
    } as Group);
  });

  // Filter by topic client-side (Firestore array-contains works but we want flexibility)
  if (topic) {
    groups = groups.filter((g) => g.topics?.includes(topic));
  }

  // Filter by search client-side for case-insensitive matching
  if (search) {
    const searchLower = search.toLowerCase();
    groups = groups.filter(
      (g) =>
        g.name.toLowerCase().includes(searchLower) ||
        g.description?.toLowerCase().includes(searchLower)
    );
  }

  return groups;
}

export async function fetchGroupById(groupId: string): Promise<Group | null> {
  const groupRef = doc(db, 'groups', groupId);
  const docSnap = await getDoc(groupRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    type: data.type || 'general',
  } as Group;
}

export async function insertGroup(
  newGroup: NewGroup,
  userId: string
): Promise<Group> {
  const groupsRef = collection(db, 'groups');

  const groupData: any = {
    name: newGroup.name,
    description: newGroup.description || null,
    image: newGroup.image || null,
    type: newGroup.type,
    topics: newGroup.topics || [],
    membersCount: 1,
    createdBy: userId,
    createdAt: serverTimestamp(),
  };

  // Add type-specific fields based on zone type
  if (newGroup.type === 'server' && newGroup.serverFields) {
    groupData.serverFields = newGroup.serverFields;
  }
  if (newGroup.type === 'project' && newGroup.projectFields) {
    groupData.projectFields = newGroup.projectFields;
  }
  if (newGroup.type === 'guild' && newGroup.guildFields) {
    groupData.guildFields = newGroup.guildFields;
  }
  if (newGroup.type === 'feedback' && newGroup.feedbackFields) {
    groupData.feedbackFields = newGroup.feedbackFields;
  }

  const docRef = await addDoc(groupsRef, groupData);

  return {
    id: docRef.id,
    name: newGroup.name,
    description: newGroup.description,
    image: newGroup.image,
    type: newGroup.type,
    topics: newGroup.topics,
    membersCount: 1,
    createdAt: new Date() as any,
  };
}
